import {PADDLE_CONFIG, SUBSCRIPTION_FAILED_STATES} from '/src/const';
import type {TBillingCycle} from './pricing.types';
import type {ILocalizedPlanPrice} from './PricingPlans2.prices';
import {CURRENCY} from './pricing.constants';

type TCheckoutBillingCycle = ReturnType<typeof getCheckoutBillingCycle>;
type TCheckoutOfferQuery = Record<string, string | null | (string | null)[] | undefined>;
type TPaidPlan = 'hobby' | 'plus';
type TSubscriptionPlan = 'HOBBY' | 'PLUS' | string | null | undefined;
type TActiveSubscriptionChangeAction = 'current' | 'self_serve' | 'manual';
type TPricingSubscription = {
    status?: string | null;
    ends_at?: string | null;
};

function getPaidPlanRank(plan: TSubscriptionPlan | TPaidPlan): number | undefined {
    const normalized = plan?.toLowerCase();
    if (normalized === 'hobby') return 1;
    if (normalized === 'plus') return 2;
    return undefined;
}

export function getCheckoutBillingCycle(billingCycle: TBillingCycle): 'week' | 'month' | 'annual' | 'lifetime' {
    if (billingCycle === 'weekly') return 'week';
    if (billingCycle === 'monthly') return 'month';
    if (billingCycle === 'lifetime') return 'lifetime';
    return 'annual';
}

function isCanceledStatus(status?: string | null): boolean {
    return status === 'canceled' || status === 'cancelled';
}

function hasFutureEndsAt(endsAt?: string | null, now = new Date()): boolean {
    if (!endsAt) return false;
    const endDate = new Date(endsAt);
    return Number.isFinite(endDate.getTime()) && endDate.getTime() > now.getTime();
}

export function isPricingSubscriptionActive(
    subscription: TPricingSubscription | null | undefined,
    now = new Date(),
): boolean {
    if (!subscription) return false;
    if (SUBSCRIPTION_FAILED_STATES.includes(subscription.status ?? '')) return false;
    if (isCanceledStatus(subscription.status)) return hasFutureEndsAt(subscription.ends_at, now);
    return true;
}

export function stripZeroCents(value: string): string {
    return value.replace(/[.,]00$/, '');
}

export function getPaddlePriceId(plan: 'hobby' | 'plus', cycle: TBillingCycle): string | undefined {
    if (cycle === 'weekly') {
        return plan === 'plus' ? PADDLE_CONFIG.prices.plus.week : undefined;
    }
    if (cycle === 'lifetime') {
        return plan === 'plus' ? PADDLE_CONFIG.prices.plus.lifetime : undefined;
    }
    const checkoutCycle = getCheckoutBillingCycle(cycle);
    const planPrices = PADDLE_CONFIG.prices[plan] as Partial<Record<TCheckoutBillingCycle, string>>;
    return planPrices[checkoutCycle];
}

export function getLocalizedPrice(
    plan: 'hobby' | 'plus',
    cycle: TBillingCycle,
    localizedPrices: Record<string, ILocalizedPlanPrice>,
): ILocalizedPlanPrice | undefined {
    const priceId = getPaddlePriceId(plan, cycle);
    return priceId ? localizedPrices[priceId] : undefined;
}

export function formatMonthlyEquivalent(yearlyPrice: ILocalizedPlanPrice | undefined): string {
    if (yearlyPrice) {
        const num = Number(yearlyPrice.rawTotal) / 100;
        if (!isNaN(num)) {
            const formatted = new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: yearlyPrice.currencyCode,
            }).format(num / 12);
            return stripZeroCents(formatted);
        }
    }
    return '-';
}

function formatLocalizedCurrency(amount: number, currencyCode: string): string {
    return stripZeroCents(
        new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
        }).format(amount),
    );
}

export function formatYearlySavings(
    monthlyPrice: ILocalizedPlanPrice | undefined,
    yearlyPrice: ILocalizedPlanPrice | undefined,
): string | undefined {
    if (!monthlyPrice || !yearlyPrice) return undefined;

    const monthlyTotal = Number(monthlyPrice.rawTotal) / 100;
    const yearlyTotal = Number(yearlyPrice.rawTotal) / 100;
    if (!Number.isFinite(monthlyTotal) || !Number.isFinite(yearlyTotal)) return undefined;

    const savings = monthlyTotal * 12 - yearlyTotal;
    if (savings <= 0) return undefined;

    return formatLocalizedCurrency(Math.trunc(savings), yearlyPrice.currencyCode);
}

export function formatPlanYearlySavings(
    monthlyPrice: number | 'FREE',
    yearlyPrice: number | 'FREE',
): string | undefined {
    if (typeof monthlyPrice !== 'number' || typeof yearlyPrice !== 'number') return undefined;

    const savings = monthlyPrice * 12 - yearlyPrice;
    if (savings <= 0) return undefined;

    return `${CURRENCY}${Math.trunc(savings)}`;
}

export function loginHash(plan: string, billingCycle: TBillingCycle): string {
    if (billingCycle === 'lifetime') return `#buy_${plan}_lifetime`;
    if (billingCycle === 'yearly') return `#buy_${plan}_annual`;
    if (billingCycle === 'weekly') return `#buy_${plan}_week`;
    return `#buy_${plan}`;
}

export function getCheckoutOfferCode(query: TCheckoutOfferQuery): string | undefined {
    const rawOffer = Array.isArray(query.offer) ? query.offer[0] : query.offer;
    if (!rawOffer) return undefined;

    const normalizedOffer = rawOffer.trim().toUpperCase();
    if (!/^[A-Z0-9_-]{1,64}$/.test(normalizedOffer)) return undefined;

    return normalizedOffer;
}

export function buildLoginCheckoutPath(
    plan: 'hobby' | 'plus',
    billingCycle: TBillingCycle,
    offerCode?: string,
): string {
    const query = offerCode ? `?offer=${encodeURIComponent(offerCode)}` : '';
    return `/login${query}${loginHash(plan, billingCycle)}`;
}

export function buildBuyNowCheckoutPath(isBuyNow: string, offerCode?: string): string {
    const query = offerCode ? `?offer=${encodeURIComponent(offerCode)}` : '';
    return `/buy_${isBuyNow}${query}`;
}

export function buildSubscriptionChangePath(
    plan: TPaidPlan,
    billingCycle: Exclude<TBillingCycle, 'weekly' | 'lifetime'>,
    offerCode?: string,
): string {
    const params = new URLSearchParams({plan, cycle: billingCycle});
    const normalizedOfferCode = getCheckoutOfferCode({offer: offerCode});
    if (normalizedOfferCode) {
        params.set('offer', normalizedOfferCode);
    }
    return `/upgrade?${params.toString()}`;
}

export function getActiveSubscriptionChangeAction(
    currentPlan: TSubscriptionPlan,
    currentBillingCycle: TBillingCycle | 'unknown',
    targetPlan: TPaidPlan,
    targetBillingCycle: TBillingCycle,
): TActiveSubscriptionChangeAction {
    if (currentPlan?.toLowerCase() === targetPlan && currentBillingCycle === targetBillingCycle) return 'current';
    if (targetBillingCycle === 'weekly' || targetBillingCycle === 'lifetime') return 'manual';
    if (currentBillingCycle === 'yearly' && targetBillingCycle === 'monthly') return 'manual';
    const currentPlanRank = getPaidPlanRank(currentPlan);
    const targetPlanRank = getPaidPlanRank(targetPlan);
    if (currentPlanRank && targetPlanRank && targetPlanRank < currentPlanRank) return 'manual';
    return 'self_serve';
}
